import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Play } from 'lucide-react';
import type { Wrapped } from '@techsofit/api-types';
import { Button, Logo } from '@techsofit/ui';
import { WrappedView } from '../components/WrappedView';
import { GlowUpStory } from '../components/GlowUpStory';
import { saveNodeAsImage } from '../lib/share';

/** Public, no-login Wrapped recap opened from a share link (/w/:token). */
export function WrappedPublicPage() {
  const { token = '' } = useParams();
  const [wrapped, setWrapped] = useState<Wrapped | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    fetch(`/api/v1/public/wrapped/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => active && (setWrapped(d), setState('ok')))
      .catch(() => active && setState('error'));
    return () => {
      active = false;
    };
  }, [token]);

  async function onSave() {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      await saveNodeAsImage(cardRef.current, `${(wrapped?.memberName ?? 'progress').replace(/\s+/g, '-').toLowerCase()}-glow-up.png`);
    } catch {
      // ignore — image generation can fail if photos block cross-origin canvas
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh bg-canvas px-4 py-10">
      <div className="mx-auto max-w-md">
        {state === 'loading' && <div className="py-24 text-center text-sm text-muted">Loading…</div>}
        {state === 'error' && (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <p className="text-sm text-muted">We couldn’t load this Glow Up. Double-check the link.</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        )}
        {state === 'ok' && wrapped && (
          <>
            <div className="mb-4 flex justify-center">
              <Button size="lg" onClick={() => setPlaying(true)}>
                <Play className="size-5" aria-hidden /> Play Glow Up
              </Button>
            </div>
            <WrappedView ref={cardRef} wrapped={wrapped} />
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={onSave} loading={saving}>
                <Download className="size-4" aria-hidden /> Save image
              </Button>
            </div>
            <div className="mt-8 flex flex-col items-center gap-1.5">
              <Logo />
              <p className="text-xs text-muted">Fitness progress, tracked with TechSoFit.</p>
              <a
                href="https://www.techsolutely.co.zw"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted underline decoration-line-strong underline-offset-2 hover:text-ink"
              >
                A Techsolutely Innovations product
              </a>
            </div>

            {/* Growth loop: a member's shared Glow Up is also a pitch to other gym owners. */}
            <a
              href="/pricing?ref=glowup"
              className="mt-6 block rounded-card border border-line bg-card p-5 text-center transition-shadow hover:shadow-card-hover"
            >
              <p className="font-display text-lg font-semibold text-ink">Run a gym or studio?</p>
              <p className="mx-auto mt-1 max-w-xs text-sm text-muted">
                Give every member a Glow Up like this — goals, check-ins, reminders, and shareable progress.
              </p>
              <span className="mt-4 inline-flex h-touch items-center justify-center rounded-pill bg-accent px-6 font-medium text-accent-ink">
                Start free with TechSoFit
              </span>
            </a>

            {playing && <GlowUpStory wrapped={wrapped} onClose={() => setPlaying(false)} onSaveImage={onSave} />}
          </>
        )}
      </div>
    </div>
  );
}
